/**
 * DiscoverPage - Guided Trip Planning Chat with Map
 * 
 * Desktop: Split layout (map left, chat right)
 * Mobile: Chat only + map modal button
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import {
    sendDiscoveryMessage,
    resetDiscovery,
    generateTrip,
    DiscoveryState,
    ApiError
} from '../services/api';
import { logger } from '../utils/logger';
import DiscoveryMap, { DestinationLocation } from '../components/discover/DiscoveryMap';
import CaptchaModal from '../components/common/CaptchaModal';
import { GeneratingModal } from '../components/discover/GeneratingModal';
import UnlockModal from '../components/UnlockModal';
import { useUserStatus } from '../hooks/useUserStatus';
import './DiscoverPage.css';

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
}


// Simple geocoding for common destinations (MVP - extends naturally)
const DESTINATION_COORDS: Record<string, { lat: number; lng: number }> = {
    'spania': { lat: 40.4168, lng: -3.7038 },
    'spain': { lat: 40.4168, lng: -3.7038 },
    'italia': { lat: 41.9028, lng: 12.4964 },
    'italy': { lat: 41.9028, lng: 12.4964 },
    'grecia': { lat: 37.9838, lng: 23.7275 },
    'greece': { lat: 37.9838, lng: 23.7275 },
    'franța': { lat: 48.8566, lng: 2.3522 },
    'france': { lat: 48.8566, lng: 2.3522 },
    'paris': { lat: 48.8566, lng: 2.3522 },
    'roma': { lat: 41.9028, lng: 12.4964 },
    'rome': { lat: 41.9028, lng: 12.4964 },
    'venetia': { lat: 45.4408, lng: 12.3155 },
    'veneția': { lat: 45.4408, lng: 12.3155 },
    'venice': { lat: 45.4408, lng: 12.3155 },
    'venezia': { lat: 45.4408, lng: 12.3155 },
    'barcelona': { lat: 41.3851, lng: 2.1734 },
    'madrid': { lat: 40.4168, lng: -3.7038 },
    'atena': { lat: 37.9838, lng: 23.7275 },
    'athens': { lat: 37.9838, lng: 23.7275 },
    'londra': { lat: 51.5074, lng: -0.1278 },
    'london': { lat: 51.5074, lng: -0.1278 },
    'amsterdam': { lat: 52.3676, lng: 4.9041 },
    'berlin': { lat: 52.5200, lng: 13.4050 },
    'viena': { lat: 48.2082, lng: 16.3738 },
    'vienna': { lat: 48.2082, lng: 16.3738 },
    'praga': { lat: 50.0755, lng: 14.4378 },
    'prague': { lat: 50.0755, lng: 14.4378 },
    'budapesta': { lat: 47.4979, lng: 19.0402 },
    'budapest': { lat: 47.4979, lng: 19.0402 },
    'dubai': { lat: 25.2048, lng: 55.2708 },
    'bali': { lat: -8.4095, lng: 115.1889 },
    'santorini': { lat: 36.3932, lng: 25.4615 },
    'lisabona': { lat: 38.7223, lng: -9.1393 },
    'lisbon': { lat: 38.7223, lng: -9.1393 },
    'porto': { lat: 41.1579, lng: -8.6291 },
    'croatia': { lat: 45.1, lng: 15.2 },
    'croația': { lat: 45.1, lng: 15.2 },
    'turcia': { lat: 39.9334, lng: 32.8597 },
    'turkey': { lat: 39.9334, lng: 32.8597 },
    'istanbul': { lat: 41.0082, lng: 28.9784 },
    'egipt': { lat: 26.8206, lng: 30.8025 },
    'egypt': { lat: 26.8206, lng: 30.8025 },
    'maldive': { lat: 3.2028, lng: 73.2207 },
    'maldives': { lat: 3.2028, lng: 73.2207 },
    'japonia': { lat: 35.6762, lng: 139.6503 },
    'japan': { lat: 35.6762, lng: 139.6503 },
    'tokyo': { lat: 35.6762, lng: 139.6503 },
    // Romanian cities
    'bucurești': { lat: 44.4268, lng: 26.1025 },
    'bucharest': { lat: 44.4268, lng: 26.1025 },
    'cluj': { lat: 46.7712, lng: 23.6236 },
    'cluj-napoca': { lat: 46.7712, lng: 23.6236 },
    'iași': { lat: 47.1585, lng: 27.6014 },
    'iasi': { lat: 47.1585, lng: 27.6014 },
    'timișoara': { lat: 45.7489, lng: 21.2087 },
    'timisoara': { lat: 45.7489, lng: 21.2087 },
    'constanța': { lat: 44.1598, lng: 28.6348 },
    'constanta': { lat: 44.1598, lng: 28.6348 },
    'brașov': { lat: 45.6427, lng: 25.5887 },
    'brasov': { lat: 45.6427, lng: 25.5887 },
    'sibiu': { lat: 45.7983, lng: 24.1256 },
    'oradea': { lat: 47.0722, lng: 21.9217 },
    'arad': { lat: 46.1866, lng: 21.3123 },
    'suceava': { lat: 47.6635, lng: 26.2732 },
    'sighișoara': { lat: 46.2197, lng: 24.7912 },
    'sighisoara': { lat: 46.2197, lng: 24.7912 },
    'baia mare': { lat: 47.6567, lng: 23.5850 },
    'mamaia': { lat: 44.2659, lng: 28.6189 },
    'sinaia': { lat: 45.3496, lng: 25.5510 },
    'românia': { lat: 45.9432, lng: 24.9668 },
    'romania': { lat: 45.9432, lng: 24.9668 },
    // Popular islands
    'tenerife': { lat: 28.2916, lng: -16.6291 },
    'mallorca': { lat: 39.6953, lng: 3.0176 },
    'ibiza': { lat: 38.9067, lng: 1.4206 },
    'creta': { lat: 35.2401, lng: 24.8093 },
    'crete': { lat: 35.2401, lng: 24.8093 },
};

export default function DiscoverPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { userStatus } = useUserStatus();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [phase, setPhase] = useState<DiscoveryState['phase']>(1);
    const [canGenerate, setCanGenerate] = useState(false);
    const [summary, setSummary] = useState('');
    const [preferences, setPreferences] = useState<Record<string, unknown>>({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [destination, setDestination] = useState<DestinationLocation | null>(null);
    const [showMapModal, setShowMapModal] = useState(false);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [pendingTripId, setPendingTripId] = useState<string | null>(null); // For async generation
    const pendingSlugRef = useRef<string | null>(null); // Store slug when ready, for redirect when modal shows
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Extract destination from messages (local dictionary only - fast & language-agnostic)
    const extractDestination = useCallback((text: string): DestinationLocation | null => {
        const lower = text.toLowerCase();

        // Check local dictionary for known cities
        for (const [name, coords] of Object.entries(DESTINATION_COORDS)) {
            if (lower.includes(name)) {
                return {
                    name: name.charAt(0).toUpperCase() + name.slice(1),
                    ...coords,
                    isSuggested: false,
                };
            }
        }

        return null;
    }, []);

    // Scroll to bottom on new messages
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Reset session from backend and local UI on page load
    useEffect(() => {
        const initSession = async () => {
            // Reset backend session
            await resetDiscovery();

            // Reset local UI state
            setPhase(1);
            setCanGenerate(false);
            setSummary('');
            setPreferences({});
            setDestination(null);
            setPendingTripId(null);
            setMessages([{
                role: 'assistant',
                content: t('discover.welcome', { defaultValue: 'Bună! Sunt Raisa, asistentul tău de călătorii. \n\nCe fel de experiență îți dorești: relaxare la plajă, explorare culturală sau să îndeplinești un vis anume?' })
            }]);
        };
        initSession();

        // Check for initial message from URL
        const urlParams = new URLSearchParams(window.location.search);
        const initialMsg = urlParams.get('msg');
        if (initialMsg) {
            window.history.replaceState({}, '', window.location.pathname);
            setInput(initialMsg);
            setTimeout(() => {
                sendInitialMessage(initialMsg);
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Send message helper
    const sendMessage = async (msg: string) => {
        setMessages(prev => [...prev, { role: 'user', content: msg }]);
        setInput('');
        setIsLoading(true);
        setError('');

        // If user is answering patch question (pendingTripId exists), handle redirect
        if (pendingTripId) {
            setSuggestions([]);
            setPendingTripId(null);

            // Check if trip is already ready (slug stored in ref)
            const langPrefix = i18n.language?.startsWith('ro') ? '/ro' : '';
            if (pendingSlugRef.current) {
                // Trip ready - redirect immediately
                navigate({ to: `${langPrefix}/trip/${pendingSlugRef.current}` });
                pendingSlugRef.current = null;
            } else {
                // Trip still generating - show modal, polling will redirect when ready
                setIsGenerating(true);

                // Start a secondary poll that WILL redirect
                const checkInterval = setInterval(() => {
                    if (pendingSlugRef.current) {
                        clearInterval(checkInterval);
                        navigate({ to: `${langPrefix}/trip/${pendingSlugRef.current}` });
                        pendingSlugRef.current = null;
                        setIsGenerating(false);
                    }
                }, 500);

                // Timeout after 90s
                setTimeout(() => {
                    clearInterval(checkInterval);
                    if (!pendingSlugRef.current) {
                        setIsGenerating(false);
                        setError('Generarea durează prea mult. Te rog să încerci din nou.');
                    }
                }, 90000);
            }

            setIsLoading(false);
            return;
        }

        // Check for destination in user message (local dictionary)
        const dest = extractDestination(msg);
        if (dest) setDestination(dest);

        try {
            const response = await sendDiscoveryMessage(msg);
            setMessages(prev => [...prev, { role: 'assistant', content: response.reply }]);
            setPhase(response.phase);
            setSummary(response.summary);
            setSuggestions(response.suggestions || []);

            // GENERATING: All phases complete - show modal and poll for completion
            if (response.action === 'GENERATING' && response.tripId) {
                setIsGenerating(true);
                setSuggestions([]);

                // Start polling for trip completion
                const pollInterval = setInterval(async () => {
                    try {
                        const statusRes = await fetch(
                            `${import.meta.env.VITE_API_URL || '/api'}/chat/discovery/status/${response.tripId}`,
                            { credentials: 'include' }
                        );
                        const status = await statusRes.json();

                        if (status.ready) {
                            clearInterval(pollInterval);
                            // Redirect to trip page
                            window.location.href = `/trip/${status.slug}`;
                        } else if (status.status === 'failed') {
                            clearInterval(pollInterval);
                            setIsGenerating(false);
                            setError(status.error || 'Generarea a eșuat');
                        }
                    } catch {
                        // Ignore polling errors
                    }
                }, 1500);

                // Cleanup after 90 seconds
                setTimeout(() => {
                    clearInterval(pollInterval);
                    if (isGenerating) {
                        setIsGenerating(false);
                        setError('Generarea durează prea mult. Te rog să încerci din nou.');
                    }
                }, 90000);
                return;
            }

            // PATCH_WHILE_GENERATING: Show patch question, generate in background (legacy)
            if (response.action === 'PATCH_WHILE_GENERATING' && response.tripId && response.patch) {
                const patch = response.patch;

                // Show patch question in chat
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: patch.question
                }]);

                // Set suggestions to patch options for quick reply (Da/Nu)
                setSuggestions(patch.options || ['Da', 'Nu']);

                // Store tripId - modal will show when user answers
                setPendingTripId(response.tripId);

                // Start polling in background (trip is generating async)
                const pollInterval = setInterval(async () => {
                    try {
                        const statusRes = await fetch(
                            `${import.meta.env.VITE_API_URL || '/api'}/chat/discovery/status/${response.tripId}`,
                            { credentials: 'include' }
                        );
                        const status = await statusRes.json();

                        if (status.ready) {
                            clearInterval(pollInterval);
                            // Store slug in ref - will be used for redirect
                            pendingSlugRef.current = status.slug;
                            // Clear pendingTripId so user answer triggers redirect
                            setPendingTripId(null);
                        } else if (status.status === 'failed') {
                            clearInterval(pollInterval);
                            setIsGenerating(false);
                            setPendingTripId(null);
                            pendingSlugRef.current = null;
                            setError(status.error || 'Generarea a eșuat');
                        }
                    } catch {
                        // Ignore polling errors
                    }
                }, 1500);

                // Cleanup after 90 seconds
                setTimeout(() => clearInterval(pollInterval), 90000);
                return;
            }

            // FCG: Fast Commit Generation - auto-create trip when minimum data collected (fallback)
            if (response.action === 'GENERATE_TRIP' && response.preferences) {
                // Show generating message
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: t('discover.generating', { defaultValue: '✨ Generez itinerariul pentru tine...' })
                }]);
                setIsLoading(true);

                try {
                    // Use generateTrip with summary as prompt
                    const result = await generateTrip(
                        response.summary,
                        { language: i18n.language.startsWith('ro') ? 'ro' : 'en', currency: 'EUR' }
                    );
                    // Redirect to trip page with language prefix
                    const langPrefix = i18n.language?.startsWith('ro') ? '/ro' : '';
                    navigate({ to: `${langPrefix}/trip/${result.trip.slug}` });
                    return;
                } catch (genError) {
                    setError(t('discover.generation_error', { defaultValue: 'A apărut o eroare la generare. Te rog să încerci din nou.' }));
                }
            } else {
                setCanGenerate(response.canGenerate);
            }

            // Note: We only extract destination from USER messages (line 237-239)
            // Not from AI responses, since AI often mentions multiple cities as suggestions
        } catch {
            setError(t('discover.error', { defaultValue: 'A apărut o eroare. Te rog să încerci din nou.' }));
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };

    const sendInitialMessage = (msg: string) => sendMessage(msg);

    const handleSend = () => {
        const trimmed = input.trim();
        if (!trimmed || isLoading) return;
        sendMessage(trimmed);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleGenerate = async (captchaToken?: string) => {
        if (!canGenerate || isGenerating) return;
        setIsGenerating(true);
        setError('');

        try {
            // Build a more complete prompt from summary + destination
            const destName = destination?.name || (preferences.destination as string) || '';
            const prompt = destName
                ? `${summary}. Destinație: ${destName}`
                : summary;

            const result = await generateTrip(prompt, { language: 'ro', captchaToken });

            if (result.trip?.slug) {
                window.location.href = `/trip/${result.trip.slug}`;
            } else {
                throw new Error('No trip slug returned');
            }
        } catch (err) {
            const apiError = err as ApiError;
            if (apiError.requiresCaptcha) {
                // Show captcha modal
                setShowCaptcha(true);
                setIsGenerating(false);
            } else if (apiError.error?.includes('limit reached') || apiError.error?.includes('Daily trip generation limit')) {
                // Rate limit reached - show upgrade modal
                setShowUpgradeModal(true);
                setIsGenerating(false);
            } else {
                logger.error('Generate trip failed:', err);
                setError(t('discover.generate_error', { defaultValue: 'Nu am putut genera itinerariul. Te rog să încerci din nou.' }));
                setIsGenerating(false);
            }
        }
    };

    const handleReset = async () => {
        try {
            await resetDiscovery();
            setPhase(1);
            setCanGenerate(false);
            setSummary('');
            setDestination(null);
            setMessages([{
                role: 'assistant',
                content: t('discover.welcome', { defaultValue: 'Bună! Sunt Raisa, asistentul tău de călătorii. \n\nCe fel de experiență îți dorești: relaxare la plajă, explorare culturală sau să îndeplinești un vis anume?' })
            }]);
        } catch {
            setError('Nu am putut reseta conversația.');
        }
    };


    return (
        <div className="discover-page discover-split">
            {/* Map Panel (Desktop) */}
            <div className="discover-map-panel">
                <DiscoveryMap destination={destination} />
            </div>

            {/* Chat Panel */}
            <div className="discover-chat-panel">
                {/* Header */}
                <header className="discover-header">
                    <Link to="/" className="back-link">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        <span>Acasă</span>
                    </Link>

                    <div className="phase-indicator">
                        {[1, 2, 3, 4].map((p) => (
                            <div
                                key={p}
                                className={`phase-dot ${phase === p ? 'active' : ''} ${typeof phase === 'number' && p < phase ? 'complete' : ''} ${phase === 'ready' ? 'complete' : ''}`}
                            >
                                {typeof phase === 'number' && p < phase || phase === 'ready' ? '✓' : p}
                            </div>
                        ))}
                    </div>

                    <button className="reset-btn" onClick={handleReset} title="Începe din nou">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                            <path d="M3 3v5h5" />
                        </svg>
                    </button>
                </header>

                {/* Messages */}
                <div className="discover-messages">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`discover-message ${msg.role}`}>
                            {msg.role === 'assistant' && (
                                <div className="avatar"><img src="/raisa.png" alt="Raisa" width="28" height="28" /></div>
                            )}
                            <div className="message-bubble">
                                {msg.role === 'assistant' ? (
                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                ) : (
                                    <p>{msg.content}</p>
                                )}
                            </div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="discover-message assistant">
                            <div className="avatar"><img src="/raisa.png" alt="Raisa" width="28" height="28" /></div>
                            <div className="message-bubble typing">
                                <span></span><span></span><span></span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>


                {/* Summary Card */}
                {canGenerate && summary && (
                    <div className="discovery-summary">
                        <h3>Am înțeles:</h3>
                        <p>{summary}</p>
                        <div className="summary-actions">
                            <button className="edit-btn" onClick={() => setCanGenerate(false)}>
                                Editează
                            </button>
                            <button
                                className="generate-btn"
                                onClick={() => handleGenerate()}
                                disabled={isGenerating}
                            >
                                {isGenerating ? 'Se generează...' : 'Generează itinerariul'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Error */}
                {error && <div className="discover-error">{error}</div>}

                {/* Quick Reply Suggestions */}
                {suggestions.length > 0 && !isLoading && !canGenerate && (
                    <div className="quick-replies">
                        {suggestions.map((suggestion, idx) => (
                            <button
                                key={idx}
                                className="quick-reply-btn"
                                onClick={() => {
                                    setInput(suggestion);
                                    sendMessage(suggestion);
                                }}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                )}

                {/* Input */}
                <div className="discover-input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        className="discover-input"
                        placeholder={t('discover.placeholder', { defaultValue: 'Scrie un mesaj...' })}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading || isGenerating}
                    />
                    <button
                        className="send-btn"
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading || isGenerating}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile Map Button */}
            <button
                className="mobile-map-btn"
                onClick={() => setShowMapModal(true)}
            >
                🗺️ Vezi harta
            </button>

            {/* Mobile Map Modal */}
            {showMapModal && (
                <div className="map-modal-overlay" onClick={() => setShowMapModal(false)}>
                    <div className="map-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="map-modal-close" onClick={() => setShowMapModal(false)}>
                            ✕
                        </button>
                        <DiscoveryMap destination={destination} />
                    </div>
                </div>
            )}

            {/* Captcha Modal */}
            <CaptchaModal
                isOpen={showCaptcha}
                onVerify={(token) => handleGenerate(token)}
                onClose={() => setShowCaptcha(false)}
            />

            {/* Unlock Modal - Rate Limit */}
            <UnlockModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                userStatus={userStatus}
                trigger="chat"
            />

            {/* Generating Modal - Async Trip Generation */}
            <GeneratingModal
                isOpen={isGenerating}
                destination={destination?.name}
            />
        </div>
    );
}
